import { api } from './client';

export interface GeofenceSite {
  id?: string;
  name: string;
  latitude: number;
  longitude: number;
  radiusMeters: number;
}

export interface GeofenceStatus {
  enabled: boolean;
  sites?: GeofenceSite[];
  office: GeofenceSite | null;
  matchedSite?: GeofenceSite | null;
  withinRange: boolean | null;
  distanceMeters: number | null;
  canClockIn: boolean;
  canClockOut?: boolean;
  remoteAvailable?: boolean;
  remoteSite?: { id: string; name: string } | null;
}

export interface AttendanceLocation {
  latitude?: number | null;
  longitude?: number | null;
  siteName?: string | null;
  mapUrl?: string | null;
}

export interface OpenSession {
  id?: string;
  date: string;
  clockInTime: string;
  acknowledgedAt?: string | null;
}

export interface AttendanceToday {
  isClockedIn: boolean;
  isClockedOut: boolean;
  clockInTime: string | null;
  clockOutTime: string | null;
  totalHours: number;
  workHours: number;
  isLate: boolean;
  lateMinutes?: number | null;
  isEarlyDeparture?: boolean;
  earlyDepartureMinutes?: number | null;
  status?: string;
  statusLabel?: string;
  notes?: string | null;
  clockInMode?: string | null;
  clockOutMode?: string | null;
  clockInOutstationReason?: string | null;
  clockOutOutstationReason?: string | null;
  clockInLocation?: AttendanceLocation | null;
  clockOutLocation?: AttendanceLocation | null;
  openSession?: OpenSession | null;
  geofence?: GeofenceStatus;
}

export interface AttendanceDayRecord {
  id?: string;
  date: string;
  clockInTime: string | null;
  clockOutTime: string | null;
  clockInMode: string | null;
  clockOutMode: string | null;
  requiresAttention?: boolean;
  workHours: number;
  totalHours?: number;
  status: string;
  statusLabel?: string;
  isLate: boolean;
  lateMinutes?: number | null;
  isEarlyDeparture: boolean;
  earlyDepartureMinutes?: number | null;
  clockInLocation?: AttendanceLocation | null;
  clockOutLocation?: AttendanceLocation | null;
  employee?: { id: string; fullName: string };
}

export interface AttendanceSummaryStats {
  totalDays: number;
  presentDays: number;
  lateDays: number;
  earlyDepartureDays: number;
  totalWorkHours: number;
  totalHours: number;
}

export interface AttendanceSummary {
  month: number;
  year: number;
  summary: AttendanceSummaryStats;
  attendances: AttendanceDayRecord[];
}

type Coordinates = { latitude: number; longitude: number };

export function getAttendanceToday(coords?: Coordinates) {
  const sp = new URLSearchParams();
  if (coords) {
    sp.set('latitude', String(coords.latitude));
    sp.set('longitude', String(coords.longitude));
  }
  const q = sp.toString() ? `?${sp}` : '';
  return api<{ data: AttendanceToday }>(`/attendance/today${q}`);
}

export function getGeofenceStatus(coords?: Coordinates) {
  const sp = new URLSearchParams();
  if (coords) {
    sp.set('latitude', String(coords.latitude));
    sp.set('longitude', String(coords.longitude));
  }
  const q = sp.toString() ? `?${sp}` : '';
  return api<{ data: GeofenceStatus }>(`/attendance/geofence-status${q}`);
}

export type ClockInResponse = {
  clockInTime: string;
  isLate: boolean;
  lateMinutes: number;
};

export type ClockOutResponse = {
  clockOutTime: string;
  totalHours: number;
  workHours: number;
  isEarlyDeparture: boolean;
  earlyDepartureMinutes: number;
};

export function clockIn(
  coords?: Coordinates,
  notes?: string,
  remote?: boolean,
  outstation?: boolean,
  outstationReason?: string
) {
  return api<{ data: ClockInResponse }>('/attendance/clock-in', {
    method: 'POST',
    body: JSON.stringify({
      ...(coords ? { latitude: coords.latitude, longitude: coords.longitude } : {}),
      ...(notes ? { notes } : {}),
      ...(remote ? { remote: true } : {}),
      ...(outstation ? { outstation: true, outstationReason } : {}),
    }),
  });
}

export function clockOut(
  coords?: Coordinates,
  notes?: string,
  outstation?: boolean,
  outstationReason?: string
) {
  return api<{ data: ClockOutResponse }>('/attendance/clock-out', {
    method: 'POST',
    body: JSON.stringify({
      ...(coords ? { latitude: coords.latitude, longitude: coords.longitude } : {}),
      ...(notes ? { notes } : {}),
      ...(outstation ? { outstation: true, outstationReason } : {}),
    }),
  });
}

export function closeOpenSession() {
  return api<{ data: { clockOutTime: string; totalHours: number; workHours: number } }>(
    '/attendance/close-open-session',
    { method: 'POST', body: JSON.stringify({}) }
  );
}

export function acknowledgeOpenSession() {
  return api<{ message: string }>('/attendance/open-session/acknowledge', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function getAttendanceSummary(params?: { month?: number; year?: number }) {
  const sp = new URLSearchParams();
  if (params?.month) sp.set('month', String(params.month));
  if (params?.year) sp.set('year', String(params.year));
  const q = sp.toString() ? `?${sp}` : '';
  return api<{ data: AttendanceSummary }>(`/attendance/summary${q}`);
}

function monthDateRange(month: number, year: number) {
  const start = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const end = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  return { start, end };
}

export function getAttendanceRecords(params: {
  scope: 'mine' | 'team';
  month: number;
  year: number;
  per_page?: number;
}) {
  const { start, end } = monthDateRange(params.month, params.year);
  const sp = new URLSearchParams({
    scope: params.scope,
    startDate: start,
    endDate: end,
  });
  if (params.per_page != null) sp.set('per_page', String(params.per_page));

  return api<{ data: AttendanceDayRecord[] }>(`/attendance?${sp}`);
}

export { canViewTeamAttendance } from '@/lib/roles';
